import React, { useState, useEffect } from 'react';
import { callApi } from '@/api/callApi';
import { API_URL, API_ENDPOINT } from '@/api/constant';
import { GetFilterUrl } from '@/utility/helper';
import { Layout } from '@/components/Layout/Layout';
import { FilterBar } from '@/components/common/FilterBar';
import { MainHeading } from '@/components/common/MainHeading';
import { Range } from 'react-date-range';
import { IAnalyticsFilters } from '@/interfaces/Dashboard/dashboard.interface';

// Import New Sections
import { AnalyticsSummaryStrip } from '@/components/Analytics/AnalyticsSummaryStrip';
import { RevenueSection } from '@/components/Analytics/RevenueSection';
import { OrdersSection } from '@/components/Analytics/OrdersSection';
import { CustomersSection } from '@/components/Analytics/CustomersSection';
import { StorePerformanceSection } from '@/components/Analytics/StorePerformanceSection';
import { DriversSection } from '@/components/Analytics/DriversSection';
import { ProductSalesSection } from '@/components/Analytics/ProductSalesSection';
import { DeliverySection } from '@/components/Analytics/DeliverySection';
import { PaymentSection } from '@/components/Analytics/PaymentSection';

import {
    DollarSign, ShoppingBag, Users, Store, Truck, Package, Clock, CreditCard, Download
} from 'lucide-react';

type Section = 'revenue' | 'orders' | 'customers' | 'stores' | 'drivers' | 'products' | 'delivery' | 'payments' | 'reports';

const SECTIONS = [
    { key: 'revenue' as Section, label: 'Revenue & GMV', icon: <DollarSign className="w-4 h-4" /> },
    { key: 'orders' as Section, label: 'Orders', icon: <ShoppingBag className="w-4 h-4" /> },
    { key: 'customers' as Section, label: 'Customers', icon: <Users className="w-4 h-4" /> },
    { key: 'stores' as Section, label: 'Stores', icon: <Store className="w-4 h-4" /> },
    { key: 'drivers' as Section, label: 'Drivers', icon: <Truck className="w-4 h-4" /> },
    { key: 'products' as Section, label: 'Products', icon: <Package className="w-4 h-4" /> },
    { key: 'delivery' as Section, label: 'Delivery', icon: <Clock className="w-4 h-4" /> },
    { key: 'payments' as Section, label: 'Payments', icon: <CreditCard className="w-4 h-4" /> },
    // { key: 'reports' as Section, label: 'Reports', icon: <Download className="w-4 h-4" /> },
];

const TIME_FILTERS = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'weekly', label: 'Last 7 Days' },
    { value: 'monthly', label: 'This Month' },
    { value: 'yearly', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' },
];

export default function AnalyticsPage() {
    const [activeSection, setActiveSection] = useState<Section>('revenue');

    // Global Filter State
    const [timeFilter, setTimeFilter] = useState('monthly');
    const [customDateRange, setCustomDateRange] = useState<Range[]>([{
        startDate: new Date(), endDate: new Date(), key: 'selection'
    }]);

    const filters: IAnalyticsFilters = {
        filter: timeFilter as any,
        ...(timeFilter === 'custom' && customDateRange[0].startDate && customDateRange[0].endDate ? {
            startDate: customDateRange[0].startDate.toISOString().split('T')[0],
            endDate: customDateRange[0].endDate.toISOString().split('T')[0],
        } : {})
    };

    // Centralized Data Fetching
    const [summaryData, setSummaryData] = useState<any>(null);
    const [isSummaryLoading, setIsSummaryLoading] = useState(true);

    const [sectionData, setSectionData] = useState<any>(null);
    const [isSectionLoading, setIsSectionLoading] = useState(true);

    // Fetch summary on filter change
    useEffect(() => {
        const fetchSummary = async () => {
            setIsSummaryLoading(true);
            try {
                const paramsStr = GetFilterUrl({ ...filters, type: 'summary' } as any);
                const res = await callApi(`${API_URL}${API_ENDPOINT.reportsAnalytics}?${paramsStr}`, { method: 'GET' });
                if (res?.data) setSummaryData(res.data);
            } catch (err) { console.error(err); }
            setIsSummaryLoading(false);
        };
        fetchSummary();
    }, [filters.filter, filters.startDate, filters.endDate, filters.storeId, filters.driverId, filters.paymentMethod]);

    // Helper function to parse numeric strings for Recharts
    const parseNumericStrings = (obj: any): any => {
        if (typeof obj === 'string') {
            const num = Number(obj);
            if (!isNaN(num) && obj.trim() !== '') return num;
            return obj;
        }
        if (Array.isArray(obj)) return obj.map(parseNumericStrings);
        if (typeof obj === 'object' && obj !== null) {
            const newObj: any = {};
            for (const key in obj) {
                newObj[key] = parseNumericStrings(obj[key]);
            }
            return newObj;
        }
        return obj;
    };

    // Fetch section on filter or tab change
    useEffect(() => {
        const fetchSection = async () => {
            setIsSectionLoading(true);
            try {
                if (activeSection === 'reports') {
                    setIsSectionLoading(false);
                    return;
                }
                const paramsStr = GetFilterUrl({ ...filters, type: activeSection } as any);
                const res = await callApi(`${API_URL}${API_ENDPOINT.reportsAnalytics}?${paramsStr}`, { method: 'GET' });
                if (res?.data) {
                    const parsedData = {
                        ...res.data,
                        charts: parseNumericStrings(res.data.charts)
                    };
                    setSectionData(parsedData);
                }
            } catch (err) { console.error(err); }
            setIsSectionLoading(false);
        };
        fetchSection();
    }, [activeSection, filters.filter, filters.startDate, filters.endDate, filters.storeId, filters.driverId, filters.paymentMethod]);

    const renderActiveSection = () => {
        switch (activeSection) {
            case 'revenue': return <RevenueSection filters={filters} data={sectionData} isLoading={isSectionLoading} />;
            case 'orders': return <OrdersSection filters={filters} data={sectionData} isLoading={isSectionLoading} />;
            case 'customers': return <CustomersSection filters={filters} data={sectionData} isLoading={isSectionLoading} />;
            case 'stores': return <StorePerformanceSection filters={filters} data={sectionData} isLoading={isSectionLoading} />;
            case 'drivers': return <DriversSection filters={filters} data={sectionData} isLoading={isSectionLoading} />;
            case 'products': return <ProductSalesSection filters={filters} data={sectionData} isLoading={isSectionLoading} />;
            case 'delivery': return <DeliverySection filters={filters} data={sectionData} isLoading={isSectionLoading} />;
            case 'payments': return <PaymentSection filters={filters} data={sectionData} isLoading={isSectionLoading} />;
            default: return null;
        }
    };

    return (
        <Layout>
            <div className=" space-y-6">

                <MainHeading
                    title="Analytics Dashboard"
                    description="View comprehensive insights across revenue, orders, customers, and operations."
                />

                {/* GLOBAL FILTERS */}
                <div className="">
                    <FilterBar
                        activeFilter={timeFilter}
                        setActiveFilter={setTimeFilter}
                        customDateRange={customDateRange}
                        setCustomDateRange={setCustomDateRange}
                        selectedStoreId={""}
                        setSelectedStoreId={() => { }}
                        selectedStatus={""}
                        setSelectedStatus={() => { }}
                        stores={[]}
                    />
                </div>

                {/* SUMMARY STRIP */}
                <AnalyticsSummaryStrip filters={filters} data={summaryData} isLoading={isSummaryLoading} />

                {/* TAB NAVIGATION */}
                <div className="bg-card rounded-2xl border shadow-sm p-2 flex overflow-x-auto no-scrollbar scroll-smooth">
                    <div className="flex gap-1 min-w-max">
                        {SECTIONS.map((s) => {
                            const isActive = activeSection === s.key;
                            return (
                                <button
                                    key={s.key}
                                    onClick={() => setActiveSection(s.key)}
                                    className={`
                                        flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
                                        ${isActive
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-muted-foreground hover:bg-secondary/40 hover:text-foreground'
                                        }
                                    `}
                                >
                                    {s.icon}
                                    {s.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ACTIVE SECTION CONTENT */}
                <div className="min-h-[500px]">
                    {renderActiveSection()}
                </div>
            </div>
        </Layout>
    );
}
